import type { PopoverInterface, PopoverOptions } from "flowbite";
import { Popover } from "flowbite";
import moment from "moment-timezone";
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import cable from "@/lib/cable.js";
import { EventBus } from "@/lib/event_bus";
import { generateUID } from "@/lib/helpers";
import provideHook from "@/lib/provideHook";
import { getQueryVariables, updateQueryParam } from "@/lib/url";

import AdminTimeClockChannel from "@/channels/AdminTimeClockChannel.js";
import { breakpoints } from "@/mixins";
import { jobShowPath, userShowPath } from "@/mixins/path_helpers";
import EventCalendar from "@/mobiscroll/EventCalendar";
import { useScheduleColors } from "@/SchedulerV2/useScheduleColors";
import { AdminPreference } from "@/typings/adminPreferences";
import { TimelineEvent } from "@/typings/scheduler";
import { EventAPIResponse, ResourceAPIResponse } from "@/typings/scheduler";
import {
  MbscEventcalendarOptions,
  MbscEventClickEvent
} from "@mobiscroll/javascript";
import { getUnavailableColorsFromBusinessHours } from "./getUnavailableColorsForResource";

// Interface for using EventCalendar
export const useEventCalendar = provideHook(() => {
  const store = useStore();
  const { getStatusCustomizationName } = useScheduleColors();
  const calendar = ref<EventCalendar | null>(null);
  const activeResource = ref<ResourceAPIResponse | null>(null);
  const eventPoll = ref<ReturnType<typeof setInterval> | null>(null);
  const selectedAppointmentId = ref<string | number | undefined | null>(null);
  const channel = ref<AdminTimeClockChannel | null>(null);
  const selectedEventId = ref<string | number | undefined | null>(null);

  const isVisible = (event: EventAPIResponse) => {
    const { jobStatusIds, departmentIds, serviceCategoryIds, statuses } =
      filters.value;

    if (jobStatusIds.length) {
      if (!jobStatusIds.includes(event.appointment?.job_status?.key || ""))
        return false;
    }

    if (departmentIds.length && event.technician) {
      if (!departmentIds.includes(event.technician?.department.id))
        return false;
    }

    if (serviceCategoryIds.length) {
      if (!serviceCategoryIds.includes(event.appointment?.service_category_id))
        return false;
    }

    if (statuses.length) {
      const status = getStatusCustomizationName(event.appointment);
      if (!statuses.includes(status)) return false;
    }

    return true;
  };
  const filters = computed(() => store.state.timeline.filters);
  const events = computed<EventAPIResponse[]>(() => {
    const allEvents: EventAPIResponse[] = [
      ...store.state.timeline.events
    ].reduce((_events: EventAPIResponse[], event: EventAPIResponse) => {
      if (event.appointment?.ghost) {
        _events.push({
          ...event,
          appointment: {
            ...event.appointment,
            ghost: false,
            unassigned: false
          },
          technician: event.ghost_technician,
          is_ghost: true,
          id: generateUID()
        });
      }
      _events.push(event);
      return _events;
    }, []);
    return allEvents.filter((event) => isVisible(event));
  });
  const unassignedRowEnabled = computed(() => {
    const preference: AdminPreference | undefined =
      store.state.adminPreferences.find(
        (preference: AdminPreference) =>
          preference.key === "sheduler_unassigned_events_floating"
      );
    return !preference?.enabled;
  });
  const resources = computed<
    | ResourceAPIResponse[]
    | { id: number; full_name: string; avatar: any; department: any }[]
  >(() => {
    const resources:
      | ResourceAPIResponse
      | { id: number; full_name: string; avatar: any; department: any }[] = [];
    if (unassignedRowEnabled.value) {
      resources.push({
        id: -1,
        full_name: "Unassigned",
        avatar: {},
        department: {}
      });
    }
    const filteredTechs = filters.value?.departmentIds.length
      ? store.state.timeline.resources.filter((tech: ResourceAPIResponse) => {
          return filters.value.departmentIds.some((deptId) =>
            tech.belongs_to_departments.includes(deptId)
          );
        })
      : store.state.timeline.resources;
    return resources.concat(
      filteredTechs
        .slice()
        .sort((a: ResourceAPIResponse, b: ResourceAPIResponse) =>
          a.full_name < b.full_name ? -1 : 1
        )
    );
  });
  const unassignedEvents = computed(() => {
    if (!calendar.value) return [];
    return events.value
      .filter((event) => event.appointment && !event.technician)
      .map((event) => {
        return new TimelineEvent({
          id: event.id,
          title: calendar.value?.getEventTitle(event) || "",
          start: calendar.value?.getEventStart(event) || "",
          end: calendar.value?.getEventEnd(event) || "",
          resource: event.technician?.id || -1,
          ghost: Boolean(event.appointment.ghost),
          editable: event.appointment.status === "pending",
          details: event
        });
      });
  });
  watch(
    events,
    (val, oldVal) => {
      if (val !== oldVal) {
        calendar.value?.setEvents(val);
      }
    },
    { deep: true }
  );
  watch(resources, (val, oldVal) => {
    if (JSON.stringify(val) !== JSON.stringify(oldVal)) {
      setResources(val);
      // applyTechnicianOffHourHighlights(val);
      // Apply active resource query param on mobile only
      if (val.length > oldVal.length && breakpoints().sm) {
        const params = getQueryVariables();
        if (params.resource) {
          activeResource.value = resources.value.find(
            (resource: ResourceAPIResponse) =>
              resource.id.toString() === params.resource
          );
          if (activeResource.value) {
            setActiveResource(activeResource.value);
          }
        }
      }
    }
  });

  const getDateFilters = (date) => {
    const startOfDay = moment(date).startOf("day");
    const endOfDay = moment(date).endOf("day");
    return { start_date: startOfDay, end_date: endOfDay };
  };

  const sortResources = (resources: ResourceAPIResponse[]) => {
    resources.slice().sort((a, b) => (a.full_name < b.full_name ? -1 : 1));
    return resources;
  };

  const destroyCalendar = () => {
    calendar.value?.destroy();

    stopEventPolling();
    EventBus.$off("refresh-schedule-board", refreshTimeline);
  };

  const fetchTimelineData = (filters: { start_date: Date; end_date: Date }) => {
    return store.dispatch("fetchTimelineEvents", {
      start_date: filters.start_date.toISOString(),
      end_date: filters.end_date.toISOString()
    });
  };

  const fetchResources = () => {
    store.dispatch("fetchTimelineResources");
  };

  const initCalendar = (
    node: HTMLElement | null,
    date: Date,
    options?: MbscEventcalendarOptions
  ) => {
    if (node) {
      const zoomLevel = localStorage.getItem("scheduler-zoom") || 50;
      calendar.value = new EventCalendar({
        node,
        options: { ...options, selectedDate: date, zoomLevel }
      });
      loadData(getDateFilters(date));

      startEventPolling();
      connectToChannel();
      // Register edit event click
      window.handleSchedulerEventEdit = (appointmentId) => {
        selectedAppointmentId.value = appointmentId;
        if (window.closeUnassignedModal) {
          window.closeUnassignedModal();
        }
      };
      EventBus.$on("refresh-schedule-board", refreshTimeline);
      applyFilterParam();
    }
  };

  const loadData = (filters: { start_date: Date; end_date: Date }) => {
    // Check if date in store is different than current date before fetching data
    if (store.state.timeline.start_date !== filters.start_date.toISOString()) {
      fetchTimelineData(filters);
      fetchResources();
    } else {
      calendar.value?.navigate(store.state.timeline.start_date);
      calendar.value?.setEvents(events.value);
      setResources(resources.value);
    }
  };

  const setActiveResource = (resource: ResourceAPIResponse) => {
    activeResource.value = resource;
    calendar.value?.setResources([resource]);
    updateQueryParam(
      "resource",
      resource.id === -1 ? null : resource.id.toString()
    );
  };

  const setResources = (resources: ResourceAPIResponse[]) => {
    if (breakpoints().sm) {
      const sortedResources = sortResources(resources);
      // Auto select unassigned if no active resource
      if (
        !activeResource.value ||
        !sortedResources.find((res) => res.id === activeResource.value?.id)
      ) {
        activeResource.value = sortedResources[0];
        calendar.value?.setResources([sortedResources[0]]);
      }
    } else {
      calendar.value?.setResources(resources);
    }
  };

  const applyTechnicianOffHourHighlights = (
    resources: ResourceAPIResponse[]
  ) => {
    console.log("calculate hours");
    const allColors = resources.flatMap((tech) =>
      [0, 1, 2, 3, 4, 5, 6].flatMap((day) =>
        getUnavailableColorsFromBusinessHours(
          tech.business_hours,
          day,
          tech.id,
          window.currentTenant.timezone
        )
      )
    );

    // Pass into Mobiscroll config
    calendar.value?.setOptions({ colors: allColors });
  };

  const setCalendarDate = async (date: Date) => {
    // Ignore same day
    if (moment(getRefDate()).utc().isSame(moment(date).utc(), "day")) return;
    calendar.value?.navigate(date);
    EventBus.$emit("update-schedule-header-date", date);
    await fetchTimelineData(getDateFilters(date));
  };

  const getRefDate = () => {
    const viewDate =
      calendar.value?.getViewDate() || store.state.timeline.start_date;
    if (!viewDate) return new Date();
    return viewDate;
  };
  const setCalendarHours = (totalHours: number) => {
    let options = {};
    switch (totalHours) {
      case 6:
        options = { startTime: "09:00", endTime: "15:00" };
        break;
      case 8:
        options = { startTime: "08:00", endTime: "16:00" };
        break;
      case 10:
        options = { startTime: "07:00", endTime: "17:00" };
        break;
      case 14:
        options = { startTime: "05:00", endTime: "19:00" };
        break;
      case 16:
        options = { startTime: "04:00", endTime: "20:00" };
        break;
      case 18:
        options = { startTime: "03:00", endTime: "21:00" };
        break;
      default:
        options = { startTime: "06:00", endTime: "18:00" }; //12hours
    }
    calendar.value?.setOptions({
      refDate: getRefDate(),
      view: {
        timeline: {
          ...calendar.value.desktopViewSettings.timeline,
          ...options
        }
      }
    });
  };
  const refreshTimeline = () => {
    fetchTimelineData(getDateFilters(store.state.timeline.start_date));
  };
  const setCalendarZoom = (zoomLevel: 25 | 50 | 75 | 100) => {
    calendar.value?.setOptions({
      refDate: getRefDate(),
      zoomLevel: zoomLevel
    });
    refreshTimeline();
    localStorage.setItem("scheduler-zoom", zoomLevel);
  };
  const reRenderResources = () => {
    setResources(resources.value);
  };
  const startEventPolling = () => {
    stopEventPolling();
    // Update schedule every 2 minutes
    eventPoll.value = setInterval(() => {
      refreshTimeline();
    }, 120_000);
  };
  const stopEventPolling = () => {
    if (eventPoll.value) {
      clearInterval(eventPoll.value);
    }
  };
  const connectToChannel = () => {
    if (!store.state.tenant?.time_cards_enabled) return;
    channel.value = new AdminTimeClockChannel();

    if (channel.value) {
      cable
        .subscribe(channel.value)
        .then(() => {
          channel.value?.on(
            "tenant-clock-in-status-change",
            handleStatusChange
          );
        })
        .catch((err) => {
          console.log("App Unable to join Cable server", err);
        });
    }
  };

  const handleStatusChange = (payload) => {
    const { user_id, action } = payload;
    if (action === "clock_in") {
      $(`.resource-cell-${user_id} .clocked-in-status-icon`).removeClass(
        "bg-red-500"
      );
      $(`.resource-cell-${user_id} .clocked-in-status-icon`).addClass(
        "bg-green-500"
      );
    } else {
      $(`.resource-cell-${user_id} .clocked-in-status-icon`).removeClass(
        "bg-green-500"
      );
      $(`.resource-cell-${user_id} .clocked-in-status-icon`).addClass(
        "bg-red-500"
      );
    }
  };
  const setTimezoneLabel = () => {
    setTimeout(() => {
      const timeIndicator = document.querySelector(
        ".mbsc-schedule-time-indicator-time"
      );
      if (
        timeIndicator &&
        !timeIndicator.contains(document.querySelector(".timezone-label"))
      ) {
        const timezone = document.createElement("span");
        timezone.classList.add("timezone-label");
        timezone.innerHTML = ` (${window.currentTenant.timezone_abbreviation})`;
        timeIndicator.appendChild(timezone);
      }
    });
  };
  const setStartParameter = (date) => {
    // Don't set URL param for Today
    if (!moment(moment(date)).isSame(new Date(), "day")) {
      const calendarDate = moment(date).utc().format("YYYY-MM-DD");
      updateQueryParam("start", calendarDate);
    } else {
      updateQueryParam("start", null);
    }
  };
  const handleEventRightClick = (args: MbscEventClickEvent) => {
    args.domEvent.preventDefault();
    const event = args.event.details;
    if (!event?.appointment) return;
    const content = document.createElement("div");
    content.innerHTML = `
      <div class="bg-white shadow rounded-lg border p-2 *:cursor-pointer *:p-2 *:rounded-lg">
        <div class="hover:bg-blue-50" onclick="handleEdit(${
          event.appointment.id
        })">Edit</div>
        <div class="hover:bg-blue-50 ${
          event.appointment.status !== "pending" ? "text-gray-400" : ""
        }" onclick="${
      event.appointment.status === "pending"
        ? `handleEdit(${event.appointment.id})`
        : ""
    }">Cancel</div>
        <a class="hover:bg-blue-50 block no-underline text-gray-900" href="${jobShowPath(
          event.appointment.job_id,
          event.appointment.id
        )}">View Appointment</a>
        <a class="hover:bg-blue-50 block no-underline text-gray-900" href="${userShowPath(
          event.appointment.customer.id
        )}">View Customer</a>
      </div>
      `;
    document.body.appendChild(content);

    // options with default values
    const options: PopoverOptions = {
      placement: "bottom-end",
      triggerType: "click",
      onHide: () => {
        popover.destroy();
      }
    };
    const popover: PopoverInterface = new Popover(
      content,
      args.domEvent.target,
      options
    );

    popover.show();
  };

  const handleEventClick = (args: MbscEventClickEvent) => {
    const event = args.event.details;
    if (!event?.appointment) {
      return (selectedEventId.value = args.event.id);
    }
    selectedAppointmentId.value = event.appointment.id;
  };

  const applyFilterParam = () => {
    const params = getQueryVariables();
    const filters: {
      departmentIds?: number[];
      serviceCategoryIds?: number[];
      statuses?: string[];
      jobStatusIds?: string[];
    } = {};
    if (params.departmentIds) {
      filters.departmentIds = params.departmentIds
        .split(",")
        .map((id) => parseInt(id, 10));
    }
    if (params.serviceCategoryIds) {
      filters.serviceCategoryIds = params.serviceCategoryIds
        .split(",")
        .map((id) => parseInt(id, 10));
    }
    if (params.statuses) {
      filters.statuses = params.statuses.split(",");
    }
    if (params.jobStatusIds) {
      filters.jobStatusIds = params.jobStatusIds.split(",");
    }
    store.dispatch("setTimelineFilters", filters);
  };

  return {
    activeResource,
    calendar,
    events,
    resources,
    unassignedEvents,
    selectedAppointmentId,
    selectedEventId,

    destroyCalendar,
    initCalendar,
    setActiveResource,
    setCalendarDate,
    setCalendarHours,
    setCalendarZoom,
    refreshTimeline,
    fetchResources,
    reRenderResources,
    setTimezoneLabel,
    setStartParameter,
    handleEventRightClick,
    handleEventClick
  };
});
